export type BackgroundWorkKind =
  | 'persistent_work'
  | 'delivery_commitment'
  | 'consultation'
  | 'conversation_watch'
  | 'parallel_analysis'
  | 'subagent'
  // Durable v1 events remain replayable after the semantic v2 rollout.
  | 'conductor'
  | 'attention'

export type BackgroundWorkStatus =
  | 'unknown'
  | 'starting'
  | 'running'
  | 'watching'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'blocked'
  | 'needs_user'
  | 'cancelled'

export interface BackgroundWorkPresentation {
  executionOnly?: boolean
  statusObservedAt?: string
  statusValidUntil?: string
  schemaVersion: string
  kind: BackgroundWorkKind
  status: BackgroundWorkStatus
  label: string
  title?: string
  expectedDeliverable?: string
  detail?: string
  runId?: string
  taskId?: string
  sourceRef?: string
  workKey?: string
  dueAt?: string
  nextCheckAt?: string
  currentStep?: string
  channelLabel?: string
  facets?: string[]
  routeStatus?: 'agent_session' | 'internal' | 'unresolved' | 'unavailable'
  deliveryOwner?: 'agent'
}

export function mergeBackgroundWork(
  current: BackgroundWorkPresentation | undefined,
  next: BackgroundWorkPresentation | undefined,
): BackgroundWorkPresentation | undefined {
  if (!next) return current
  if (!current) return next
  if (current.executionOnly && current.runId && next.runId && current.runId !== next.runId) return next

  const merged = Object.fromEntries(
    Object.entries({ ...current, ...next }).filter(([, value]) => value !== undefined && value !== ''),
  ) as unknown as BackgroundWorkPresentation

  // A late accepted tool result cannot overwrite an authoritative runtime
  // snapshot, including 'unknown'. New runtime snapshots may resolve unknown.
  const currentTime = Date.parse(current.statusObservedAt || '') || 0
  const nextTime = Date.parse(next.statusObservedAt || '') || 0
  const olderNativeSnapshot = current.executionOnly && next.executionOnly && (
    nextTime < currentTime || (nextTime === currentTime && current.status === 'unknown' && next.status === 'running')
  )
  if ((current.executionOnly && !next.executionOnly) || olderNativeSnapshot
    || (isTerminal(current.status) && !isTerminal(next.status))) {
    merged.status = current.status
    merged.label = current.label
    merged.detail = current.detail
    merged.routeStatus = current.routeStatus
    merged.statusObservedAt = current.statusObservedAt
    merged.statusValidUntil = current.statusValidUntil
    if (current.executionOnly) merged.executionOnly = true
  }
  if (!next.taskId && !next.runId && current.title) merged.title = current.title
  return merged
}

function isTerminal(status: BackgroundWorkStatus): boolean {
  return ['completed', 'failed', 'blocked', 'cancelled'].includes(status)
}
